import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  DISC_FORMATS,
  formatMonth,
  formatMonthShort,
  formatNumber,
  type DiscFormat,
  type MonthBucket,
} from '@physical-movies/core';
import { useI18n } from '../app/context';
import { useFormatLabels } from '../lib/labels';
import styles from './charts.module.css';

/**
 * Every disc on the shelf, by format, as one proportional strip.
 *
 * It is the same idea as the spine on a poster tile, at collection scale: the
 * shape of your shelf in one line.
 */
export function ShelfStrip({ counts }: { counts: Record<DiscFormat, number> }) {
  const { t, locale } = useI18n();
  const labels = useFormatLabels();

  const total = DISC_FORMATS.reduce((sum, format) => sum + counts[format], 0);
  if (total === 0) return <p className={styles.empty}>{t('common.none')}</p>;

  const present = DISC_FORMATS.filter((format) => counts[format] > 0);

  return (
    <div>
      <div
        className={styles.strip}
        role="img"
        aria-label={present.map((format) => `${labels.long(format)}: ${counts[format]}`).join(', ')}
      >
        {present.map((format) => {
          const share = counts[format] / total;
          return (
            <div
              key={format}
              className={styles.stripSegment}
              data-format={format}
              style={{ flexGrow: share }}
              title={`${labels.long(format)} — ${counts[format]}`}
            />
          );
        })}
      </div>
      {/* The counts live in the legend, in ink. Setting them inside the
          segments would put white text on amber, which misses the contrast
          floor for text this size. */}
      <div className={styles.legend}>
        {present.map((format) => (
          <span key={format} className={styles.legendItem}>
            <span className={styles.legendSwatch} data-format={format} aria-hidden="true" />
            {labels.long(format)}
            <span className={styles.legendValue}>{formatNumber(counts[format], locale)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Watches per month over the last year.
 *
 * One series, so no legend. The busiest month is labelled directly; the rest
 * are read by hovering or focusing a column.
 */
export function MonthColumns({
  buckets,
  describe,
}: {
  buckets: MonthBucket[];
  describe(bucket: MonthBucket, monthLabel: string): string;
}) {
  const { locale } = useI18n();
  const [active, setActive] = useState<number | null>(null);

  const peak = Math.max(1, ...buckets.map((bucket) => bucket.watches));
  const peakIndex = buckets.findIndex((bucket) => bucket.watches === peak);

  return (
    <div>
      <div className={styles.columns}>
        {buckets.map((bucket, index) => {
          const monthLabel = formatMonth(bucket.month, locale);
          const description = describe(bucket, monthLabel);
          return (
            <div
              key={bucket.month}
              className={`${styles.column} ${active === index ? styles.columnActive : ''}`}
              tabIndex={0}
              role="img"
              aria-label={description}
              onPointerEnter={() => setActive(index)}
              onPointerLeave={() => setActive(null)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            >
              {index === peakIndex && bucket.watches > 0 && active !== index && (
                <span className={styles.columnValue}>{bucket.watches}</span>
              )}
              <span
                className={`${styles.bar} ${bucket.watches === 0 ? styles.barEmpty : ''}`}
                style={{ height: `${(bucket.watches / peak) * 100}%` }}
              />
              {active === index && (
                <span
                  className={styles.tooltip}
                  // Pull the tooltip in at the ends so it can never push the
                  // page sideways on a phone.
                  data-align={index < 2 ? 'start' : index > buckets.length - 3 ? 'end' : 'mid'}
                >
                  {description}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className={styles.axis} aria-hidden="true">
        {buckets.map((bucket, index) => (
          <span key={bucket.month} className={styles.axisLabel}>
            {/* Every third month keeps the axis readable on a phone. */}
            {index % 3 === 0 ? formatMonthShort(bucket.month, locale) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface RankItem {
  key: string;
  label: string;
  value: number;
  /** Rendered next to the label — "4 · 8 h". */
  valueLabel: string;
  to?: string;
}

/** Ordered magnitude with a direct label on every row, so no tooltip is needed. */
export function RankBars({ items, empty }: { items: RankItem[]; empty: ReactNode }) {
  if (items.length === 0) return <p className={styles.empty}>{empty}</p>;

  const peak = Math.max(1, ...items.map((item) => item.value));

  return (
    <div className={styles.ranks}>
      {items.map((item) => {
        const row = (
          <>
            <span className={styles.rankLabel}>{item.label}</span>
            <span className={styles.rankValue}>{item.valueLabel}</span>
            <span className={styles.rankTrack}>
              {/* A zero draws nothing at all — a minimum-width stub would read
                  as "a little" rather than "none". */}
              {item.value > 0 && (
                <span
                  className={styles.rankFill}
                  style={{ width: `${(item.value / peak) * 100}%` }}
                />
              )}
            </span>
          </>
        );

        return item.to ? (
          <Link key={item.key} to={item.to} className={styles.rank}>
            {row}
          </Link>
        ) : (
          <div key={item.key} className={styles.rank}>
            {row}
          </div>
        );
      })}
    </div>
  );
}
